import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { forgotPasswordSchema } from '../schemas/auth';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Dual-layer warning state
  const [showFirstWarning, setShowFirstWarning] = useState(false);
  const [showFinalWarning, setShowFinalWarning] = useState(false);

  const theme = useTheme();
  const { themeMode } = useThemeMode();
  const { sendPasswordResetEmail } = useAuth();

  const handleInitialSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setValidationError('');

    const validationResult = forgotPasswordSchema.safeParse({ email });
    if (!validationResult.success) {
      setValidationError(validationResult.error.issues[0]?.message || 'Invalid email format');
      return;
    }

    setShowFirstWarning(true);
  };

  const handleFirstWarningConfirm = () => {
    setShowFirstWarning(false);
    setShowFinalWarning(true);
  };

  const handleCancelWarning = () => {
    setShowFirstWarning(false);
    setShowFinalWarning(false);
  };

  const handleFinalSubmit = async () => {
    setShowFinalWarning(false);
    try {
      setLoading(true);
      await sendPasswordResetEmail(email);
      setMessage(
        'If an account exists with this email address, you will receive password reset instructions shortly.'
      );
    } catch (_error) {
      setError('There was an error processing your request. Please try again later.');
    }
    setLoading(false);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <img
            src={
              themeMode === 'dark'
                ? '/Bidlogiq_Logo-Long_Reversed.png'
                : '/Bidlogiq_Logo-Long_Full_colours.png'
            }
            alt="BIDLOGIQ Logo"
            style={{ height: '75px', width: 'auto', marginBottom: '8px' }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              textAlign: 'center',
              fontSize: '1.5rem',
            }}
          >
            Reset Password
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleInitialSubmit} sx={{ mt: 1, width: '100%' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Important:</strong> Changing your password will lock your encrypted data. You
            will need your <strong>Recovery Code</strong> to unlock it after resetting your
            password.
          </Alert>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!validationError}
            helperText={validationError}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              mt: 3,
              mb: 2,
              color: 'white',
            }}
            disabled={loading}
          >
            <Typography
              sx={{ fontSize: '1.1rem', fontWeight: 500, color: theme.palette.bidlogiq.black }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Typography>
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <RouterLink
              to="/login"
              style={{
                color: theme.palette.bidlogiq?.camel,
                textDecoration: 'none',
                fontSize: '0.9rem',
              }}
            >
              Back to Login
            </RouterLink>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={showFirstWarning}
        onClose={handleCancelWarning}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon />
          Warning: Data Encryption Lock
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Because BIDLOGIQ uses secure end-to-end encryption, your password is required to decrypt
            your data.
          </DialogContentText>
          <DialogContentText paragraph>
            If you reset your password, <strong>you must have your Recovery Code</strong> to regain
            access to your existing encrypted files and tender drafts.
          </DialogContentText>
          <DialogContentText paragraph>
            Do you have your Recovery Code saved in a safe place?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelWarning} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleFirstWarningConfirm} color="error" variant="contained">
            Yes, I Understand
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showFinalWarning}
        onClose={handleCancelWarning}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon />
          Final Confirmation
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            If you do not have your Recovery Code, resetting your password will permanently lock you
            out of all your previous encrypted data.
          </Alert>
          <Typography variant="body1" paragraph>
            Are you absolutely sure you want to send a password reset link to{' '}
            <strong>{email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelWarning} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleFinalSubmit} color="error" variant="contained">
            Yes, Send Reset Link
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ForgotPasswordPage;
